import { useState, useEffect, useCallback } from 'react';
import {
  getPlcSettings, updatePlcSettings,
  getAppRegisters, updateAppRegisters,
  getNetworkInfo,
  getErrorMessage,
} from '../api/config';
import { useAuth } from '../context/AuthContext';

const APP_LABELS = {
  barriere: 'Barrière Automatique',
  surveillance: 'Surveillance',
  feu_tricolore: 'Feux Tricolores',
  reservoir: 'Gestion du Réservoir',
};

// ── Conversion objet {bit: nom} <-> tableau [{bit, name}] (plus facile à éditer en formulaire) ─
const bitsObjectToRows = (bitsObj) =>
  Object.entries(bitsObj || {})
    .map(([bit, name]) => ({ bit: Number(bit), name }))
    .sort((a, b) => a.bit - b.bit);

const rowsToBitsObject = (rows) => {
  const obj = {};
  for (const row of rows) obj[row.bit] = row.name;
  return obj;
};

// ── Vérifie si deux IP sont sur le même sous-réseau /24 (les 3 premiers octets) ─
const isSameSubnet = (ip1, ip2) => {
  if (!ip1 || !ip2) return true; // pas assez d'info pour juger → pas d'alerte
  const parts1 = ip1.split('.');
  const parts2 = ip2.split('.');
  if (parts1.length !== 4 || parts2.length !== 4) return true;
  return parts1[0] === parts2[0] && parts1[1] === parts2[1] && parts1[2] === parts2[2];
};

export default function ConfigPanel() {
  const { token } = useAuth();

  // ── Section 1 : connexion PLC ──────────────────────────────
  const [plcForm, setPlcForm] = useState({ host: '', port: 502, unit_id: 1, timeout: 3 });
  const [plcLoading, setPlcLoading] = useState(true);
  const [plcError, setPlcError] = useState('');
  const [plcSuccess, setPlcSuccess] = useState('');
  const [plcSubmitting, setPlcSubmitting] = useState(false);
  const [machineIp, setMachineIp] = useState('');

  useEffect(() => {
    getPlcSettings(token)
      .then(setPlcForm)
      .catch((err) => setPlcError(getErrorMessage(err, 'Impossible de charger les réglages PLC.')))
      .finally(() => setPlcLoading(false));

    getNetworkInfo(token)
      .then((data) => { if (data.local_ip) setMachineIp(data.local_ip); })
      .catch(() => {}); // détection silencieuse — pas bloquant si ça échoue
  }, [token]);

  const handlePlcChange = (field, value) => {
    setPlcForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePlcSubmit = async (e) => {
    e.preventDefault();
    setPlcError('');
    setPlcSuccess('');

    if (!plcForm.host.trim()) {
      setPlcError("L'adresse IP du PLC est obligatoire.");
      return;
    }

    setPlcSubmitting(true);
    try {
      const payload = {
        host: plcForm.host.trim(),
        port: Number(plcForm.port),
        unit_id: Number(plcForm.unit_id),
        timeout: Number(plcForm.timeout),
      };
      const res = await updatePlcSettings(token, payload);
      setPlcForm(res);
      setPlcSuccess('Réglages PLC enregistrés.');
    } catch (err) {
      setPlcError(getErrorMessage(err, "Impossible d'enregistrer les réglages PLC."));
    } finally {
      setPlcSubmitting(false);
    }
  };

  const subnetMismatch = !isSameSubnet(machineIp, plcForm.host);

  // ── Section 2 : registres par application ──────────────────
  const [selectedApp, setSelectedApp] = useState('barriere');
  const [commandRegister, setCommandRegister] = useState(0);
  const [commandBits, setCommandBits] = useState([]);
  const [statusRegisters, setStatusRegisters] = useState([]);
  const [regLoading, setRegLoading] = useState(true);
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState('');
  const [regSubmitting, setRegSubmitting] = useState(false);

  const loadRegisters = useCallback((app) => {
    setRegLoading(true);
    setRegError('');
    setRegSuccess('');
    getAppRegisters(token, app)
      .then((data) => {
        setCommandRegister(data.command_register);
        setCommandBits(bitsObjectToRows(data.command_bits));
        setStatusRegisters(
          (data.status_registers || []).map((reg) => ({
            address: reg.address,
            bits: bitsObjectToRows(reg.bits),
          }))
        );
      })
      .catch((err) => setRegError(getErrorMessage(err, 'Impossible de charger les registres.')))
      .finally(() => setRegLoading(false));
  }, [token]);

  useEffect(() => {
    loadRegisters(selectedApp);
  }, [selectedApp, loadRegisters]);

  // -- commande : bits --
  const addCommandBit = () => setCommandBits((prev) => [...prev, { bit: 0, name: '' }]);
  const updateCommandBit = (index, field, value) =>
    setCommandBits((prev) => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  const removeCommandBit = (index) =>
    setCommandBits((prev) => prev.filter((_, i) => i !== index));

  // -- statut : registres et bits --
  const addStatusRegister = () =>
    setStatusRegisters((prev) => [...prev, { address: 0, bits: [] }]);
  const removeStatusRegister = (regIndex) =>
    setStatusRegisters((prev) => prev.filter((_, i) => i !== regIndex));
  const updateStatusRegisterAddress = (regIndex, value) =>
    setStatusRegisters((prev) =>
      prev.map((reg, i) => i === regIndex ? { ...reg, address: value } : reg)
    );
  const addStatusBit = (regIndex) =>
    setStatusRegisters((prev) =>
      prev.map((reg, i) => i === regIndex ? { ...reg, bits: [...reg.bits, { bit: 0, name: '' }] } : reg)
    );
  const updateStatusBit = (regIndex, bitIndex, field, value) =>
    setStatusRegisters((prev) =>
      prev.map((reg, i) => i !== regIndex ? reg : {
        ...reg,
        bits: reg.bits.map((row, j) => j === bitIndex ? { ...row, [field]: value } : row),
      })
    );
  const removeStatusBit = (regIndex, bitIndex) =>
    setStatusRegisters((prev) =>
      prev.map((reg, i) => i !== regIndex ? reg : {
        ...reg,
        bits: reg.bits.filter((_, j) => j !== bitIndex),
      })
    );

  const handleRegistersSubmit = async (e) => {
    e.preventDefault();
    setRegError('');
    setRegSuccess('');

    if (commandBits.some((row) => !row.name.trim())) {
      setRegError('Chaque bit du registre de commande doit avoir un nom.');
      return;
    }
    if (statusRegisters.some((reg) => reg.bits.some((row) => !row.name.trim()))) {
      setRegError('Chaque bit des registres de statut doit avoir un nom.');
      return;
    }

    setRegSubmitting(true);
    try {
      const payload = {
        command_register: Number(commandRegister),
        command_bits: rowsToBitsObject(commandBits),
        status_registers: statusRegisters.map((reg) => ({
          address: Number(reg.address),
          bits: rowsToBitsObject(reg.bits),
        })),
      };
      await updateAppRegisters(token, selectedApp, payload);
      setRegSuccess('Registres enregistrés.');
    } catch (err) {
      setRegError(getErrorMessage(err, "Impossible d'enregistrer les registres."));
    } finally {
      setRegSubmitting(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="home-header">
        <h1>Panneau de Configuration</h1>
        <p>Connexion au PLC et mapping des registres Modbus</p>
        <span className="scada-badge">S C A D A</span>
      </div>

      {/* ── Connexion PLC ────────────────────────────────── */}
      <div className="admin-card config-section">
        <h2>Connexion PLC</h2>

        {plcLoading ? (
          <p className="admin-empty">Chargement…</p>
        ) : (
          <form onSubmit={handlePlcSubmit} className="config-form-grid">
            {plcError && <div className="auth-error config-full-row">{plcError}</div>}
            {plcSuccess && <div className="auth-success config-full-row">{plcSuccess}</div>}

            <div className="auth-field">
              <label>Adresse IP du PLC</label>
              <input
                type="text"
                value={plcForm.host}
                onChange={(e) => handlePlcChange('host', e.target.value)}
                placeholder="ex : 192.168.0.4"
              />
            </div>

            <div className="auth-field">
              <label>IP de la machine (ce PC)</label>
              <input
                type="text"
                value={machineIp}
                onChange={(e) => setMachineIp(e.target.value)}
                placeholder="ex : 192.168.0.10"
                className={subnetMismatch ? 'input-error' : ''}
              />
              {subnetMismatch && (
                <div className="field-warning">
                  ⚠️ Cette machine ({machineIp || '—'}) ne semble pas être sur le même
                  sous-réseau que le PLC ({plcForm.host || '—'}). La connexion Modbus
                  risque d'échouer.
                </div>
              )}
            </div>

            <div className="auth-field">
              <label>Port Modbus TCP</label>
              <input
                type="number"
                value={plcForm.port}
                onChange={(e) => handlePlcChange('port', e.target.value)}
              />
            </div>

            <div className="auth-field">
              <label>ID esclave Modbus (Unit ID)</label>
              <input
                type="number"
                value={plcForm.unit_id}
                onChange={(e) => handlePlcChange('unit_id', e.target.value)}
              />
            </div>

            <div className="auth-field">
              <label>Timeout (secondes)</label>
              <input
                type="number"
                value={plcForm.timeout}
                onChange={(e) => handlePlcChange('timeout', e.target.value)}
              />
            </div>

            <button type="submit" className="btn-primary config-full-row" disabled={plcSubmitting}>
              {plcSubmitting ? 'Enregistrement…' : 'Enregistrer la connexion PLC'}
            </button>
          </form>
        )}
      </div>

      {/* ── Registres par application ───────────────────── */}
      <div className="admin-card config-section">
        <h2>Registres Modbus</h2>

        <div className="auth-field config-app-select">
          <label>Application</label>
          <select value={selectedApp} onChange={(e) => setSelectedApp(e.target.value)}>
            {Object.entries(APP_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        {regLoading ? (
          <p className="admin-empty">Chargement…</p>
        ) : (
          <form onSubmit={handleRegistersSubmit}>
            {regError && <div className="auth-error">{regError}</div>}
            {regSuccess && <div className="auth-success">{regSuccess}</div>}

            {/* Registre de commande */}
            <div className="register-block">
              <div className="register-block-header">
                <h3>Registre de commande</h3>
                <div className="register-address-field">
                  <label>Adresse</label>
                  <input
                    type="number"
                    value={commandRegister}
                    onChange={(e) => setCommandRegister(e.target.value)}
                  />
                </div>
              </div>

              {commandBits.map((row, i) => (
                <div className="register-bit-row" key={i}>
                  <input
                    type="number"
                    className="bit-index-input"
                    value={row.bit}
                    min={0}
                    max={15}
                    onChange={(e) => updateCommandBit(i, 'bit', Number(e.target.value))}
                  />
                  <input
                    type="text"
                    className="bit-name-input"
                    value={row.name}
                    placeholder="nom du bit"
                    onChange={(e) => updateCommandBit(i, 'name', e.target.value)}
                  />
                  <button type="button" className="btn-danger-link" onClick={() => removeCommandBit(i)}>
                    Retirer
                  </button>
                </div>
              ))}
              <button type="button" className="btn-add-row" onClick={addCommandBit}>
                + Ajouter un bit
              </button>
            </div>

            {/* Registres de statut */}
            {statusRegisters.map((reg, regIndex) => (
              <div className="register-block" key={regIndex}>
                <div className="register-block-header">
                  <h3>Registre de statut</h3>
                  <div className="register-address-field">
                    <label>Adresse</label>
                    <input
                      type="number"
                      value={reg.address}
                      onChange={(e) => updateStatusRegisterAddress(regIndex, Number(e.target.value))}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn-danger-link"
                    onClick={() => removeStatusRegister(regIndex)}
                  >
                    Retirer ce registre
                  </button>
                </div>

                {reg.bits.map((row, bitIndex) => (
                  <div className="register-bit-row" key={bitIndex}>
                    <input
                      type="number"
                      className="bit-index-input"
                      value={row.bit}
                      min={0}
                      max={15}
                      onChange={(e) => updateStatusBit(regIndex, bitIndex, 'bit', Number(e.target.value))}
                    />
                    <input
                      type="text"
                      className="bit-name-input"
                      value={row.name}
                      placeholder="nom du bit"
                      onChange={(e) => updateStatusBit(regIndex, bitIndex, 'name', e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn-danger-link"
                      onClick={() => removeStatusBit(regIndex, bitIndex)}
                    >
                      Retirer
                    </button>
                  </div>
                ))}
                <button type="button" className="btn-add-row" onClick={() => addStatusBit(regIndex)}>
                  + Ajouter un bit
                </button>
              </div>
            ))}

            <button type="button" className="btn-add-row" onClick={addStatusRegister}>
              + Ajouter un registre de statut
            </button>

            <button type="submit" className="btn-primary config-save-registers" disabled={regSubmitting}>
              {regSubmitting ? 'Enregistrement…' : `Enregistrer les registres — ${APP_LABELS[selectedApp]}`}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}